import {
  API_BASE_PATH,
  API_PATH_MAGIC,
  API_PATH_PASSWORD,
  CONTENT_TYPE_JSON,
  HEADER_CONTENT_TYPE,
  HTTP_OK_MAX,
  HTTP_OK_MIN,
  REASON_CSRF_EXPIRED,
  REASON_LABEL,
} from './constants';

export interface AuthOk {
  ok: true;
  next: string;
}

export interface AuthErr {
  ok: false;
  reason: string;
  message: string;
}

export type AuthResult = AuthOk | AuthErr;

export interface MagicLinkOk {
  ok: true;
  cooldown_ms: number;
}

export type ApiResponse<T> = T | AuthErr;

interface JsonRequestInit extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

// Map server REASON_* codes that this layer can produce on its own
// (network failure, non-JSON response, missing endpoint) into the
// same shape the typed server errors use. Keep these stable —
// `REASON_LABEL` in `constants.ts` reads them.
const REASON_NETWORK = 'invalid';
const REASON_NOT_IMPLEMENTED = 'invalid';
const REASON_NON_JSON = 'invalid';

const HEADER_ACCEPT = 'Accept';
const HEADER_XSRF = 'X-XSRF-TOKEN';
const XSRF_COOKIE = 'XSRF-TOKEN';
const HTTP_CSRF_MISMATCH = 419;
const CSRF_EXPIRED_MESSAGE =
  REASON_LABEL[REASON_CSRF_EXPIRED] ??
  'That didn’t go through — your secure session refreshed. Please try again.';

function readCookie(name: string): string {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return '';
}

/**
 * Re-prime the session's CSRF material. Any web-group GET refreshes the
 * XSRF-TOKEN cookie, and the SPA's own shell path is guaranteed to be
 * served with `Cache-Control: no-store`, so fetching the current
 * document is the cheapest reliable refresh.
 */
async function refreshCsrfCookie(): Promise<void> {
  try {
    await fetch(window.location.pathname, {
      credentials: 'same-origin',
      cache: 'no-store',
    });
  } catch {
    // Best-effort: the retry below will surface the real failure.
  }
}

async function send(path: string, init: JsonRequestInit): Promise<Response> {
  const headers = new Headers(init.headers ?? {});
  headers.set(HEADER_CONTENT_TYPE, CONTENT_TYPE_JSON);
  // Ask for JSON explicitly so the cloud's error renderers (validation,
  // CSRF/419) answer with the stable JSON contract instead of a
  // browser-oriented redirect.
  headers.set(HEADER_ACCEPT, CONTENT_TYPE_JSON);
  const xsrf = readCookie(XSRF_COOKIE);
  if (xsrf.length > 0) {
    headers.set(HEADER_XSRF, xsrf);
  }

  return fetch(`${API_BASE_PATH}${path}`, {
    credentials: 'same-origin',
    ...init,
    headers,
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });
}

async function request<T>(path: string, init: JsonRequestInit): Promise<ApiResponse<T>> {
  let response: Response;
  try {
    response = await send(path, init);

    // Stale CSRF material (the classic stale-tab failure): refresh the
    // cookie and retry ONCE with the fresh token. Only after the retry
    // also fails does the user see the friendly csrf_expired copy.
    if (response.status === HTTP_CSRF_MISMATCH) {
      await refreshCsrfCookie();
      response = await send(path, init);
      if (response.status === HTTP_CSRF_MISMATCH) {
        return {
          ok: false,
          reason: REASON_CSRF_EXPIRED,
          message: CSRF_EXPIRED_MESSAGE,
        };
      }
    }
  } catch (err) {
    return {
      ok: false,
      reason: REASON_NETWORK,
      message: err instanceof Error ? err.message : 'Network request failed.',
    };
  }

  // 404 from the cloud means this auth method is not wired on this
  // deployment yet (e.g. password / magic-link landed in a later
  // release than the SPA). Surface that distinctly so the caller can
  // hide the affected control on next render.
  if (response.status === 404) {
    return {
      ok: false,
      reason: REASON_NOT_IMPLEMENTED,
      message: 'This sign-in method is not enabled on this Fred Cloud deployment.',
    };
  }

  const text = await response.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      return {
        ok: false,
        reason: REASON_NON_JSON,
        message: 'Unexpected response from the sign-in service. Try again in a moment.',
      };
    }
  }

  if (response.status >= HTTP_OK_MIN && response.status <= HTTP_OK_MAX) {
    return parsed as T;
  }
  if (parsed && typeof parsed === 'object' && 'reason' in parsed && 'message' in parsed) {
    return parsed as AuthErr;
  }
  return {
    ok: false,
    reason: REASON_NON_JSON,
    message: `Sign-in failed (HTTP ${response.status}).`,
  };
}

export const authApi = {
  password: (email: string, password: string, next: string) =>
    request<AuthOk>(API_PATH_PASSWORD, {
      method: 'POST',
      body: { email, password, next },
    }),
  magic: (email: string, next: string) =>
    request<MagicLinkOk>(API_PATH_MAGIC, {
      method: 'POST',
      body: { email, next },
    }),
};
